import React from "react";
import { Button, Popconfirm, Space, Table, Tag, Tooltip } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import CopyableText from "components/common/CopyableText";

const statusColors = {
  ACTIVE: { bg: "#e6fffb", color: "#006d75", border: "#87e8de" },
  INACTIVE: { bg: "#fff2f0", color: "#cf1322", border: "#ffa39e" },
  EXPIRED: { bg: "#f5f5f5", color: "#595959", border: "#d9d9d9" },
};

const getTagStyle = (status) => {
  const colors = statusColors[String(status || "").toUpperCase()];
  return {
    backgroundColor: colors?.bg || "#f5f5f5",
    color: colors?.color || "#262626",
    padding: "2px 10px",
    borderRadius: "6px",
    fontSize: "0.65rem",
    border: `1px solid ${colors?.border || "#d9d9d9"}`,
    fontWeight: 600,
    cursor: "default",
  };
};

const resolveCouponId = (coupon) => coupon?.id || coupon?.couponId || coupon?.code;
const resolveDiscountType = (coupon) => coupon?.discountType || coupon?.valueType;
const resolveDiscountValue = (coupon) =>
  coupon?.discountValue ?? coupon?.value ?? coupon?.amount;
const resolveChargeCode = (coupon) => coupon?.chargeCode || coupon?.chargeItemCode;
const resolveValidFrom = (coupon) =>
  coupon?.validFrom || coupon?.startsAt || coupon?.effectiveFrom;
const resolveValidTo = (coupon) =>
  coupon?.validTo || coupon?.expiresAt || coupon?.validUntil || coupon?.effectiveTo;
const isRestrictedCoupon = (coupon) => {
  const ids =
    coupon?.restrictedUserIds ||
    coupon?.eligibleUserIds ||
    coupon?.allowedUserIds ||
    [];
  return coupon?.restrictedToUsers === true || (Array.isArray(ids) && ids.length > 0);
};

const formatCurrency = (amount, currency = "USD") => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return "-";

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "USD").toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericAmount);
  } catch (err) {
    return `${numericAmount.toFixed(2)} ${currency || "USD"}`;
  }
};

const formatDiscount = (coupon) => {
  const discountType = resolveDiscountType(coupon);
  const discountValue = Number(resolveDiscountValue(coupon));
  if (!Number.isFinite(discountValue)) return "-";

  if (discountType === "PERCENTAGE") {
    const displayValue = discountValue <= 1 ? discountValue * 100 : discountValue;
    return `${displayValue.toFixed(2)}%`;
  }

  return formatCurrency(discountValue, coupon.currency);
};

const formatDate = (value) => (value ? dayjs(value).format("YYYY-MM-DD hh:mm A") : "-");

export default function CouponsTable({
  coupons = [],
  loading,
  meta,
  onFetchCoupons,
  onEdit,
  onView,
  onToggleStatus,
}) {
  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 140,
      render: (code) => <CopyableText value={code} />,
    },
    {
      title: "Charge / Name",
      key: "chargeName",
      width: 290,
      render: (_, record) => (
        <div style={{ lineHeight: 1.25 }}>
          <div style={{ fontFamily: "monospace", fontSize: 12 }}>
            {resolveChargeCode(record) || "-"}
          </div>
          <div style={{ fontWeight: 600, marginTop: 2 }}>{record.name || "-"}</div>
        </div>
      ),
    },
    {
      title: "Discount",
      key: "discount",
      width: 100,
      render: (_, record) => formatDiscount(record),
    },
    {
      title: "Amount",
      key: "amount",
      width: 105,
      render: (_, record) =>
        formatCurrency(record.amount ?? record.balance, record.currency),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 105,
      render: (status) => (
        <Tag style={getTagStyle(status)}>{String(status || "-").toUpperCase()}</Tag>
      ),
    },
    {
      title: "Type",
      key: "type",
      width: 110,
      render: (_, record) => {
        const type = String(record.type || "USER").toUpperCase();
        return <Tag color={type === "SYSTEM" ? "geekblue" : "default"}>{type}</Tag>;
      },
    },
    {
      title: "Availability",
      key: "availability",
      width: 130,
      render: (_, record) => (
        <Tag color={isRestrictedCoupon(record) ? "purple" : "blue"}>
          {isRestrictedCoupon(record) ? "Restricted" : "Shareable"}
        </Tag>
      ),
    },
    {
      title: "Validity",
      key: "validity",
      width: 240,
      render: (_, record) => (
        <div style={{ lineHeight: 1.35, fontSize: 12, whiteSpace: "nowrap" }}>
          <div>
            <span style={{ color: "darkgreen", fontWeight: 700 }}>From: </span>
            {formatDate(resolveValidFrom(record))}
          </div>
          <div>
            <span style={{ color: "darkgreen", fontWeight: 700 }}>To: </span>
            {formatDate(resolveValidTo(record))}
          </div>
        </div>
      ),
    },
    {
      title: "Action",
      key: "actions",
      width: 150,
      render: (_, record) => {
        const isActive = String(record.status || "").toUpperCase() === "ACTIVE";
        return (
          <Space>
            <Tooltip title="View details">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  onView?.(record);
                }}
              />
            </Tooltip>
            <Tooltip title="Edit">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit(record);
                }}
              />
            </Tooltip>
            <Tooltip title={isActive ? "Deactivate" : "Activate"}>
              <Popconfirm
                title={`${isActive ? "Disable" : "Enable"} coupon?`}
                description={`This will ${isActive ? "disable" : "enable"} ${record.code || "this coupon"}.`}
                okText={isActive ? "Disable" : "Enable"}
                cancelText="Cancel"
                okButtonProps={{ danger: isActive }}
                onConfirm={(event) => {
                  event?.stopPropagation?.();
                  onToggleStatus(record);
                }}
                onCancel={(event) => event?.stopPropagation?.()}
              >
                <Button
                  size="small"
                  icon={isActive ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                  style={{ color: isActive ? "#1677ff" : "#15803d" }}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Table
      rowKey={resolveCouponId}
      columns={columns}
      dataSource={coupons}
      loading={loading}
      size="small"
      className="custom-table"
      scroll={{ x: 1000 }}
      onRow={(record) => ({
        onClick: () => onView?.(record),
        style: { cursor: "pointer" },
      })}
      pagination={{
        current: (meta?.page ?? meta?.number ?? 0) + 1,
        pageSize: meta?.size ?? 20,
        total: meta?.totalElements ?? coupons.length,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} coupons`,
      }}
      onChange={(pagination) => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        onFetchCoupons?.({
          page: pagination.current - 1,
          size: pagination.pageSize,
        });
      }}
    />
  );
}
